import React, { useContext, useCallback, useState } from "react";
import {
  Record,
  DataProviderContext,
  useRefresh,
  useTranslate,
  Confirm,
} from "react-admin";
import {
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Theme,
} from "@material-ui/core";
import { ApiManager } from "../../../common/data/ApiManager";
import DeleteIcon from "@material-ui/icons/DeleteForever";
import inflection from "inflection";
import { createCrudDeletedAction } from "../../../common/redux/actions/appData";
import { useDispatch } from "react-redux";

interface DeleteRecordMenuItemProps {
  record?: Record;
  resource?: string;
  confirmTitle: string;
  confirmContent: string;
  onClick?: (event: React.MouseEvent) => void;
  hidden?: boolean | ((record: Record) => boolean);
}

const isBoolean = (
  input?: boolean | ((record: Record) => boolean)
): input is boolean => {
  return input === true || input === false;
};

export const DeleteRecordMenuItem = ({
  record,
  resource,
  confirmTitle = "resources.common.labels.delete_title",
  confirmContent,
  onClick,
  hidden,
  ...rest
}: DeleteRecordMenuItemProps) => {
  const dataProvider = useContext(DataProviderContext) as ApiManager;
  const dispatch = useDispatch();
  const refresh = useRefresh();
  const translate = useTranslate();
  const [isOpen, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const theme = useTheme<Theme>();

  const deleteRecord = useCallback(
    (record: Record) => {
      return dataProvider.delete(resource ?? "unknown-resource", {
        id: record.id,
        previousData: record,
      });
    },
    [dataProvider, resource]
  );

  if (!record) {
    return null;
  }

  if (isBoolean(hidden)) {
    if (hidden) {
      return null;
    }
  } else {
    if (hidden && hidden(record)) {
      return null;
    }
  }

  const handleDialogClose = () => {
    setOpen(false);
  };

  const handleDelete = () => {
    setLoading(true);
    deleteRecord(record)
      .then((result) => {
        setLoading(false);
        dispatch(
          createCrudDeletedAction({
            meta: { resource: resource!, fetchResponse: "DELETE" },
            requestPayload: { id: record.id, previousData: record },
            payload: { data: result.data! },
          })
        );
        refresh();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <MenuItem
        {...rest}
        className="actions-menu-item"
        onClick={(e) => {
          setOpen(true);
          if (typeof onClick === "function") {
            onClick(e);
          }
        }}>
        <ListItemIcon>
          <DeleteIcon style={{ color: theme.palette.error.main }} />
        </ListItemIcon>
        <ListItemText>{translate("actions.delete")}</ListItemText>
      </MenuItem>
      <Confirm
        isOpen={isOpen}
        loading={loading}
        title={confirmTitle}
        content={confirmContent}
        translateOptions={{
          name: inflection.singularize(
            translate(`resources.${resource}.forcedCaseName`, {
              smart_count: 1,
              _: inflection.humanize(
                translate(`resources.${resource}.name`, {
                  smart_count: 1,
                  _: inflection.singularize(resource!),
                }),
                true
              ),
            })
          ),
          id: record.id,
        }}
        onConfirm={handleDelete}
        onClose={handleDialogClose}
      />
    </>
  );
};

DeleteRecordMenuItem.defaultProps = {
  confirmTitle: "resources.common.labels.delete_title",
  confirmContent: "ra.message.delete_content",
  icon: <DeleteIcon />,
};
